'use client'

import { Search, X } from 'lucide-react'
import { useEffect, useRef } from 'react'
import { Input } from '../../ui/input'

interface MobileSearchProps {
	isExpanded: boolean
	onExpand: () => void
	onCollapse: () => void
	searchValue: string
	onSearchChange: (value: string) => void
	onSearchKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void
}

export function MobileSearch({
	isExpanded,
	onExpand,
	onCollapse,
	searchValue,
	onSearchChange,
	onSearchKeyDown,
}: MobileSearchProps) {
	const inputRef = useRef<HTMLInputElement>(null)

	useEffect(() => {
		if (isExpanded && inputRef.current) {
			inputRef.current.focus()
		}
	}, [isExpanded])

	// Handle escape key to close
	useEffect(() => {
		const handleEscape = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				onCollapse()
			}
		}
		if (isExpanded) {
			document.addEventListener('keydown', handleEscape)
			return () => document.removeEventListener('keydown', handleEscape)
		}
	}, [isExpanded, onCollapse])

	if (isExpanded) {
		// Expanded state - full-width search input
		return (
			<div className="flex flex-1 items-center gap-2 lg:hidden">
				<div className="relative flex-1">
					<Search className="-translate-y-1/2 absolute top-1/2 left-3 h-[20px] w-[20px] text-brand-muted" />
					<Input
						ref={inputRef}
						placeholder="Search"
						value={searchValue}
						onChange={e => onSearchChange(e.target.value)}
						onKeyDown={onSearchKeyDown}
						className="h-[40px] w-full rounded-[16px] border-brand-gray bg-brand-dark pr-3 pl-10 text-[16px] text-white placeholder:text-brand-muted focus:ring-1 focus:ring-brand-blue"
					/>
				</div>
				<button
					type="button"
					onClick={onCollapse}
					className="flex h-[40px] w-[40px] shrink-0 items-center justify-center rounded-[8px] text-brand-muted transition-colors active:bg-brand-gray/50"
					aria-label="Close search"
				>
					<X className="h-[20px] w-[20px]" />
				</button>
			</div>
		)
	}

	// Collapsed state - just the search icon button
	return (
		<button
			type="button"
			onClick={onExpand}
			className="flex h-[40px] w-[40px] items-center justify-center rounded-[8px] border border-brand-gray bg-brand-dark transition-colors active:bg-brand-gray/50 md:hidden"
			aria-label="Open search"
		>
			<Search className="h-[20px] w-[20px] text-brand-muted" />
		</button>
	)
}
